/**
 * Central regular-expression catalog for core-web.
 *
 * One home for every shared/reusable regex so patterns are named, documented,
 * and defined once instead of copy-pasted. Lives in the `constants` (leaf)
 * layer so any layer can import it without a boundary violation.
 *
 * Conventions:
 *  - Each export is a distinct pattern. Where several call sites historically
 *    used *different* variants (e.g. multiple email checks), each variant keeps
 *    its own export so behaviour is preserved — we do not silently unify them.
 *  - Prefer these named constants over inline literals in new code.
 *  - CAUTION with the `g` flag: a regex literal with `/g` carries mutable
 *    `lastIndex` state. Sharing one instance is safe for stateless calls
 *    (`String#replace`, `#replaceAll`, `#split`, `#match`, `#matchAll`) but NOT
 *    for repeated `#test`/`#exec` on the same instance in a loop. Helpers below
 *    that need per-call state build a fresh `RegExp` internally.
 */

/* ────────────────────────────────────────────────────────────────────────
 * Email
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Canonical email pattern — the WHATWG/HTML5 `<input type="email">` check with
 * the domain part requiring at least one dot (rejects `user@localhost`).
 * Accepts tags, sub-domains, multi-level TLDs and IDN/punycode domains while
 * rejecting trailing/consecutive dots and hyphen-only domains.
 */
export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/**
 * Dot-atom email variant (domain dot is optional via `*`). Kept for the legacy
 * call sites that historically allowed single-label domains.
 */
export const EMAIL_REGEX_DOT_ATOM =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/** Loose "has an @ and a dot" email check. Cheap, permissive. */
export const EMAIL_SIMPLE_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Multi-recipient friendly email (allows dotted local part, multi-level TLD). */
export const EMAIL_MULTILEVEL_REGEX =
  /^[a-zA-Z0-9._%+-]+(?:\.[a-zA-Z0-9._%+-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/;

/** RFC-5322-ish email (quoted local part + IP-literal domains). */
export const EMAIL_RFC_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/** True when `value` is a valid email (trims, treats empty/nullish as invalid). */
export const isValidEmail = (value?: string | null): boolean =>
  typeof value === 'string' && EMAIL_REGEX.test(value.trim());

/* ────────────────────────────────────────────────────────────────────────
 * Phone
 * ──────────────────────────────────────────────────────────────────────── */

/** Loose international phone shape (used by Yup phone validation). */
export const PHONE_REGEX =
  /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

/* ────────────────────────────────────────────────────────────────────────
 * URLs & social profiles
 * ──────────────────────────────────────────────────────────────────────── */

/** Generic website/domain (optional scheme + www). */
export const URL_WEBSITE_REGEX =
  /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/;

/** Bare "scheme + non-space" URL. */
export const URL_SIMPLE_REGEX = /^(http|https):\/\/[^ "]+$/;

/** URL, mailto: or bare domain — used to auto-link free text. */
export const URL_OR_MAILTO_REGEX =
  /^(https?:\/\/[^\s.]+\.\S{2,}(\/\S*)?|mailto:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?)$/i;

/** URL or bare domain (no mailto). */
export const URL_OR_DOMAIN_REGEX =
  /^(https?:\/\/[^\s.]+\.\S{2,}(\/\S*)?|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?)$/i;

/** Host + TLD prefix check (domain must start the string). */
export const DOMAIN_PREFIX_REGEX = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

export const LINKEDIN_PROFILE_REGEX =
  /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company)\/[^\s/?#]+(\/)?$/;
export const LINKEDIN_IN_URL_REGEX =
  /http(s)?:\/\/(\w+\.)?linkedin\.com\/in\/([^/?]*)/;
export const GITHUB_PROFILE_REGEX =
  /^(https?:\/\/)?(www\.)?github\.com\/[a-zA-Z0-9_-]+(\/)?$/;
export const FACEBOOK_PROFILE_REGEX =
  /^(https?:\/\/)?(www\.)?facebook\.com\/[a-zA-Z0-9.]+(\/)?$/;
export const TWITTER_PROFILE_REGEX =
  /^(https?:\/\/)?(www\.)?twitter\.com\/[a-zA-Z0-9_]+(\/)?$/;
export const INSTAGRAM_PROFILE_REGEX =
  /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._]+(\/)?$/;
export const STACKOVERFLOW_PROFILE_REGEX =
  /^(https?:\/\/)?(www\.)?stackoverflow\.com\/users\/[0-9]+\/[a-zA-Z0-9_-]+(\/)?$/;

/**
 * Social/profile URL validators keyed by network. Superset of the historical
 * `REGEX_PATTERNS` map (kept key-compatible for existing callers).
 */
export const REGEX_PATTERNS = {
  email: EMAIL_REGEX,
  linkedin: LINKEDIN_PROFILE_REGEX,
  github: /^https?:\/\/(www\.)?github\.com\/[a-zA-Z0-9-_]+\/?(\?.*)?$/,
  facebook: /^https?:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9.]+\/?(\?.*)?$/,
  twitter:
    /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/[a-zA-Z0-9_]+\/?(\?.*)?$/,
  instagram: /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9._]+\/?(\?.*)?$/,
  stackoverflow:
    /^https?:\/\/(www\.)?stackoverflow\.com\/users\/\d+\/[a-zA-Z0-9_-]+\/?(\?.*)?$/,
  website: URL_WEBSITE_REGEX,
  genericUrl: URL_WEBSITE_REGEX,
} as const;

/* ────────────────────────────────────────────────────────────────────────
 * HTML
 * ──────────────────────────────────────────────────────────────────────── */

/** Any HTML tag — strip markup from a string (`replace` with ''). */
export const HTML_TAG_REGEX = /<[^>]*>/g;
/** `<img …>` tags. */
export const HTML_IMG_TAG_REGEX = /<img[^>]+>/gi;
/** `<img … src="…">` presence check. */
export const HTML_IMG_WITH_SRC_REGEX = /<img\s+[^>]*src=["'][^"']+["']/i;
/** Closing paragraph tags. */
export const HTML_CLOSING_P_REGEX = /<\/p>/g;
/** Legacy `<A HREF="…">text</A>` anchor capture. */
export const HTML_ANCHOR_REGEX = /<A\s+HREF="([^"]*)">([^<]+)<\/A>/gi;
/** Inline `style="color:red"` marker used by AI-diff highlighting. */
export const HTML_COLOR_RED_STYLE_REGEX = /style=['"]?color:red;?['"]?/gi;

/* ────────────────────────────────────────────────────────────────────────
 * Whitespace & newlines
 * ──────────────────────────────────────────────────────────────────────── */

/** One-or-more whitespace, global — collapse runs of whitespace. */
export const WHITESPACE_GLOBAL_REGEX = /\s+/g;
/** One-or-more whitespace, first match. */
export const WHITESPACE_REGEX = /\s+/;
/** Single whitespace char, global. */
export const WHITESPACE_CHAR_GLOBAL_REGEX = /\s/g;
/** Newline, global. */
export const NEWLINE_GLOBAL_REGEX = /\n/g;
/** Two-or-more newlines, global. */
export const MULTI_NEWLINE_REGEX = /\n{2,}/g;
/** Leading newlines. */
export const LEADING_NEWLINES_REGEX = /^\n+/g;
/** Trailing newlines. */
export const TRAILING_NEWLINES_REGEX = /\n+$/g;
/** Comma/whitespace separators (split a delimited list). */
export const COMMA_OR_WHITESPACE_REGEX = /[,\s]+/;

/* ────────────────────────────────────────────────────────────────────────
 * Text casing & word helpers
 * ──────────────────────────────────────────────────────────────────────── */

/** First letter of each word (capitalize/title-case via `replace`). */
export const WORD_INITIAL_REGEX = /\b\w/g;
/** First word character of the string. */
export const FIRST_WORD_CHAR_REGEX = /^\w/;
/** Non-word characters, global. */
export const NON_WORD_GLOBAL_REGEX = /\W/g;
/** Any uppercase letter (global) — e.g. camelCase splitting. */
export const UPPERCASE_GLOBAL_REGEX = /[A-Z]/g;
/** Any uppercase letter (first match). */
export const UPPERCASE_REGEX = /[A-Z]/;
/**
 * A standalone placeholder name token — a name part that is literally
 * "undefined" or "null" (case-insensitive), e.g. from a server-composed
 * `${first} ${last}` where a part was missing. Whole-token match, so real
 * words like "Nullson" are untouched. Used by `sanitizeName`. See ENG-3433.
 */
export const NAME_PLACEHOLDER_TOKEN_REGEX = /^(?:undefined|null)$/i;

/* ────────────────────────────────────────────────────────────────────────
 * Numbers, dates, times, colors, ids
 * ──────────────────────────────────────────────────────────────────────── */

/** Digits only (whole string). */
export const DIGITS_ONLY_REGEX = /^\d+$/;
/** Single digit (whole string). */
export const SINGLE_DIGIT_REGEX = /^\d$/;
/** Exactly four digits (whole string) — e.g. year / OTP. */
export const FOUR_DIGITS_REGEX = /^\d{4}$/;
/** Any digit, global. */
export const DIGIT_GLOBAL_REGEX = /\d/g;
/** Any digit, first match. */
export const DIGIT_REGEX = /\d/;
/** Non-digit characters, global — strip everything but digits. */
export const NON_DIGIT_GLOBAL_REGEX = /[^\d]/g;
/** Thousands-separator insertion point (`123456` → `123,456`). */
export const THOUSANDS_SEPARATOR_REGEX = /\B(?=(\d{3})+(?!\d))/g;
/** Hex string (no `#`). */
export const HEX_STRING_REGEX = /^[0-9A-Fa-f]+$/;
/** Hex color `#rrggbb` capture (optional `#`). */
export const HEX_COLOR_REGEX = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
/** `rgb()/rgba()` channel capture. */
export const RGB_CHANNELS_REGEX = /rgba?\((\d+),\s*(\d+),\s*(\d+)/;
/** ISO date `YYYY-MM-DD`. */
export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/** Year-month `YYYY-MM`. */
export const YEAR_MONTH_REGEX = /^\d{4}-\d{2}$/;
/** 12-hour clock time `h:mm AM/PM`. */
export const TIME_12H_REGEX = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
/** Percentage in parentheses e.g. `(12.5%)`, global. */
export const PARENS_PERCENT_REGEX = /\(\d+(\.\d+)?%\)/g;
/** Trailing parenthesized group e.g. ` (note)`. */
export const TRAILING_PARENS_REGEX = /\(([^)]+)\)$/;

/* ────────────────────────────────────────────────────────────────────────
 * Template / mustache variables
 * ──────────────────────────────────────────────────────────────────────── */

/** `{{ token }}` capture (non-greedy, multiline), global. */
export const TEMPLATE_VAR_REGEX = /{{([\s\S]+?)}}/g;
/** `{{token}}` group (whole match kept), global. */
export const TEMPLATE_VAR_GROUP_REGEX = /(\{\{.*?\}\})/g;
/** Single `{{ … }}` token (no global flag). */
export const TEMPLATE_VAR_SINGLE_REGEX = /\{\{[^}]+\}\}/;

/* ────────────────────────────────────────────────────────────────────────
 * Misc / domain-specific
 * ──────────────────────────────────────────────────────────────────────── */

/** Password "special character" set. */
/* eslint-disable no-useless-escape -- preserved byte-for-byte from original call sites */
export const PASSWORD_SPECIAL_CHAR_REGEX =
  /[~`¿¡!#$%\^&*€£@+÷=\-\[\]\\';,/{}\(\)|\\":<>\?\.\_]/;
/* eslint-enable no-useless-escape */
/** Escape a string for safe use inside a `new RegExp(...)`. */
export const REGEXP_ESCAPE_REGEX = /[.*+?^${}()|[\]\\]/g;
/** `kula_` id prefix. */
export const KULA_PREFIX_REGEX = /^kula_/;
/** No-braces/dots/whitespace token (a plain identifier). */
export const PLAIN_TOKEN_REGEX = /^[^{}.\s]+$/;
/** `…/survey/{id}` path — capture the survey id. */
export const SURVEY_ID_PATH_REGEX = /\/survey\/(\d+)/;
/** `…/survey/{id}…` path suffix — strip everything from `/survey/{id}`. */
export const SURVEY_PATH_SUFFIX_REGEX = /\/survey\/\d+.*$/;

/* ────────────────────────────────────────────────────────────────────────
 * Punctuation & separators
 * ──────────────────────────────────────────────────────────────────────── */

/** Strip periods and commas (e.g. numeric normalisation), global. */
export const DOT_COMMA_GLOBAL_REGEX = /[.,]/g;
/** Strip commas and periods — char-class-order variant of the above, global. */
export const COMMA_DOT_GLOBAL_REGEX = /[,.]/g;
/** Periods/whitespace runs, global (time normalisation → `:`). */
export const DOT_OR_SPACE_GLOBAL_REGEX = /[.\s]+/g;
/** Repeated colons, global (collapse to a single `:`). */
export const MULTI_COLON_GLOBAL_REGEX = /:+/g;
/** Leading/trailing colons, global (trim). */
export const TRIM_COLONS_REGEX = /^:+|:+$/g;
/** Comma-separated four-digit year capture e.g. `Jan 5, 2026`. */
export const COMMA_YEAR_REGEX = /, (\d{4})/;

/* ────────────────────────────────────────────────────────────────────────
 * More URL / HTML variants
 * ──────────────────────────────────────────────────────────────────────── */

/** Domain + optional path URL (`([/\w .-]*)*` tail). */
export const URL_DOMAIN_PATH_REGEX =
  /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
/** Loose in-text URL matcher (auto-link). */
/* eslint-disable no-useless-escape -- preserved byte-for-byte from original call sites */
export const URL_LOOSE_REGEX =
  /https?:\/\/[\w\-_]+(\.[\w\-_]+)+([\w\-\.,@?^=%&:/~\+#]*[\w\-\@?^=%&/~\+#])?/;
/* eslint-enable no-useless-escape */
/** Strip a leading protocol/`//host` prefix down to the path. */
export const PROTOCOL_RELATIVE_PATH_REGEX = /^(?:\/\/|[^/]+)*\//;
/** HTML open tag with optional attrs + captured inner text + optional close. */
export const HTML_TAG_WITH_CONTENT_REGEX =
  /<([a-zA-Z][\w-]*)(\s+[^>]*)?\/?>\s*([^<]*)\s*(<\/\1>)?/g;

/* ────────────────────────────────────────────────────────────────────────
 * Approval builder
 * ──────────────────────────────────────────────────────────────────────── */

/** Approval-step key capture e.g. `reviewers-step2`. */
export const APPROVAL_STEP_REGEX =
  /(reviewers|all_approve|approval_user_groups)-step(\d+)/;

/* ────────────────────────────────────────────────────────────────────────
 * Create-candidate drawer
 * ──────────────────────────────────────────────────────────────────────── */

/** Job-row field key in the create-candidate form e.g. `jobs_job_1`. */
export const CREATE_CONTACT_JOB_KEY_REGEX = /^jobs_job_(\d+)$/;

/**
 * @deprecated Use {@link PHONE_REGEX}. Kept as an alias for existing importers
 * of `@/constants/regex`.
 */
export const phoneRegExp = PHONE_REGEX;
